import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isFresh, isReload, readLastScreen, wasResumed, writeLastScreen } from '../utils/lastScreen';

// 데이터가 늦게 와서 화면이 아직 짧으면, 이만큼 기다리며 다시 시도한다 (약 4초)
const MAX_SCROLL_TRIES = 240;

const currentPath = () => window.location.pathname + window.location.search;

/**
 * 지금 보는 화면(주소·스크롤 위치)을 기억하고, 앱이 다시 처음부터 그려졌을 때
 * 스크롤 위치까지 되돌린다. 주소를 되돌리는 일은 라우터가 만들어지기 전에 해야 해서
 * utils/lastScreen 의 resumeLastScreen 이 따로 맡는다.
 */
export const useLastScreen = () => {
  const location = useLocation();
  const path = location.pathname + location.search;
  // 아래 기록 effect 가 덮어쓰기 전에, 열리던 순간의 기억을 먼저 붙잡아 둔다
  const [saved] = useState(readLastScreen);

  // 화면을 옮길 때마다 기록한다. 옮긴 직후라 스크롤은 맨 위다
  useEffect(() => {
    writeLastScreen({ path, y: 0, t: Date.now() });
  }, [path]);

  // 다른 앱으로 넘어갈 때 — 이 뒤에 휴대폰이 앱을 꺼버릴 수 있다
  useEffect(() => {
    const save = () => writeLastScreen({ path: currentPath(), y: window.scrollY, t: Date.now() });
    const onVisibility = () => { if (document.visibilityState === 'hidden') save(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', save);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', save);
    };
  }, []);

  // 스크롤 되돌리기 — 이어 열었거나 새로고침한 경우에만, 같은 화면일 때만, 한 번
  useEffect(() => {
    if (!(wasResumed() || isReload())) return undefined;
    if (!isFresh(saved, Date.now()) || saved.path !== currentPath() || !(saved.y > 0)) return undefined;

    let frame = 0;
    let tries = 0;
    let cancelled = false;
    // 기다리는 사이 직접 움직이기 시작했으면 끼어들지 않는다
    const cancel = () => { cancelled = true; };
    window.addEventListener('touchstart', cancel, { passive: true, once: true });
    window.addEventListener('wheel', cancel, { passive: true, once: true });

    const attempt = () => {
      if (cancelled) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max >= saved.y || tries >= MAX_SCROLL_TRIES) {
        window.scrollTo(0, Math.min(saved.y, Math.max(0, max)));
        return;
      }
      tries += 1;
      frame = requestAnimationFrame(attempt);
    };
    frame = requestAnimationFrame(attempt);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('wheel', cancel);
    };
    // 처음 그려질 때 한 번만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
